import { HugeiconsIcon } from '@hugeicons/react';
import { MailOpen01IconStroke } from '@/components/icons';

import { Client } from './types';
import { useForm } from '../../hooks/use-form';
import ActionButtons from '../../components/input/action-buttons';
import { useState } from 'react';
import { z } from 'zod';
import { formatZodError, isValidUrl } from '../../util/validation';
import { snack } from '../../util/Functions';
import { ClientSection } from './client-section';
import { FormFields, type FormFieldsConfig } from '../../components/form-fields';
import { Flex } from '../../components/layout';
import { Box } from '@mui/material';

/** Shared with the delivery page's Account Info tab (UX-944), which must keep the same limit. */
export const PHONE_MAX_LENGTH = 35;

const CONFIGS: FormFieldsConfig[] = [
    {
        fields: [
            {
                name: 'email',
                label: 'Email Address',
                placeholder: 'e.g. johndoe@example.com',
                maxLength: 100,
                required: true,
                infoText: undefined,
                type: 'text'
            },
            {
                name: 'bccEmail',
                label: 'BCC Email Address',
                placeholder: 'Enter BCC Email Address',
                maxLength: 100,
                required: false,
                infoText: 'BCC Email Address',
                helpTopicPath: 'client-contact-bcc-email',
                type: 'text'
            }
        ],
        columns: 2
    },
    // {
    //     fields: [
    //         {
    //             name: 'placeholder',
    //             type: 'placeholder'
    //         },
    //         {
    //             name: 'sendBccEmail',
    //             label: 'Send BCC Email',
    //             type: 'checkbox'
    //         }
    //     ],
    //     columns: 2
    // },
    {
        fields: [
            {
                name: 'phone',
                label: 'Business Number',
                placeholder: 'Enter Phone Number',
                maxLength: PHONE_MAX_LENGTH,
                required: false,
                infoText: 'Business Number',
                helpTopicPath: 'client-contact-business-number',
                type: 'text'
            },
            {
                name: 'mobile',
                label: 'Mobile Number',
                placeholder: 'Enter Mobile Number',
                maxLength: 10,
                required: false,
                type: 'text'
            }
        ],
        columns: 2
    },
    // {
    //     fields: [
    //         {
    //             name: 'placeholder',
    //             type: 'placeholder'
    //         },
    //         {
    //             name: 'sendMobileMessage',
    //             label: 'Receive Text Messages',
    //             type: 'checkbox'
    //         }
    //     ],
    //     columns: 2
    // },
    {
        fields: [
            {
                name: 'website',
                label: 'Website',
                placeholder: 'Enter Website',
                maxLength: 250,
                required: false,
                infoText: undefined,
                type: 'url'
            },
            {
                name: 'inventoryUrl',
                label: 'Inventory URL',
                placeholder: 'Enter Inventory Link',
                maxLength: 1024,
                required: false,
                infoText: undefined,
                type: 'url'
            }
        ],
        columns: 2
    }
];

const schema = z.object({
    email: z.email().min(1, 'Email is required'),
    bccEmail: z.string().optional(),
    phone: z.string().optional(),
    mobile: z.string().optional(),
    website: z
        .string()
        .refine(val => isValidUrl(val, { allowEmpty: true }), {
            message: 'Invalid URL'
        })
        .optional(),
    inventoryUrl: z
        .string()
        .refine(val => isValidUrl(val, { allowEmpty: true }), {
            message: 'Invalid URL'
        })
        .optional()
});

type ContactInformationFormValues = z.infer<typeof schema>;

const ContactInformationForm = ({
    client,
    onSave
}: {
    client: Client;
    onSave: (values: Partial<Client>) => void;
}) => {
    const [readonly, setReadOnly] = useState(true);

    const initialValues = {
        email: client.email,
        bccEmail: client.bccEmail,
        phone: client.phone,
        mobile: client.mobile,
        website: client.website,
        inventoryUrl: client.inventoryUrl
    };

    const { values, errors, isDirty, handleSubmit, reset, setFieldValue } =
        useForm<ContactInformationFormValues>({
            initialValues,
            validationMode: 'onSubmit',
            validate: values => {
                const result = schema.safeParse(values);
                if (!result.success) {
                    return formatZodError(result.error);
                }
                return {};
            }
        });

    const handleOnSave = () => {
        handleSubmit(values => {
            const cleanedValues = Object.fromEntries(
                Object.entries(values).map(([key, value]) => [key, value ? value : null])
            );
            onSave(cleanedValues);
            setReadOnly(true);
        });
    };

    const handleOnCancel = () => {
        setReadOnly(true);
        reset(initialValues);
    };

    return (
        <Flex component="form" sx={{ flexDirection: 'column', alignItems: 'normal' }}>
            {readonly ? (
                <Box onClick={() => setReadOnly(false)}>
                    <FormFields configs={CONFIGS} readOnly values={values} />
                </Box>
            ) : (
                <>
                    <FormFields
                        configs={CONFIGS}
                        values={values}
                        errors={errors as Record<string, string>}
                        eventHandlers={{
                            onChange: (name, value) => {
                                setFieldValue(name as keyof ContactInformationFormValues, value);
                            }
                        }}
                    />
                    <ActionButtons
                        isModified
                        onCancel={handleOnCancel}
                        onSave={handleOnSave}
                        isSaveDisabled={!isDirty}
                    />
                </>
            )}
        </Flex>
    );
};

export const ClientContactInformation = ({
    client,
    onSave
}: {
    client: Client;
    onSave: (values: Partial<Client>) => Promise<boolean>;
}) => {
    const handleOnSave = (values: Partial<Client>) => {
        onSave(values).then(success => {
            if (success) {
                snack('Contact Information Updated', { variant: 'success' });
            } else {
                snack('Update Failed', { variant: 'error' });
            }
        });
    };

    return (
        <ClientSection
            title="Contact Information"
            icon={<HugeiconsIcon icon={MailOpen01IconStroke} size={20} strokeWidth={1.5} />}
        >
            <ContactInformationForm client={client} onSave={handleOnSave} />
        </ClientSection>
    );
};
